import { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Search, ExternalLink, Loader2, ShoppingBag } from 'lucide-react';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';

interface VisualSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchTerms: string[];
  title: string;
  category?: string;
}

interface SearchResult {
  title: string;
  url: string;
  description: string;
  price: string | null;
  store: string;
  image: string | null;
}

export function VisualSearchSheet({ 
  open, 
  onOpenChange, 
  searchTerms, 
  title, 
  category 
}: VisualSearchSheetProps) {
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  const { settings: regionalSettings } = useRegionalSettings();
  const hasTriggeredRef = useRef(false);

  // Auto-search when sheet opens with search terms
  useEffect(() => {
    if (open && searchTerms.length > 0 && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      handleSearch();
    }
    if (!open) {
      hasTriggeredRef.current = false;
      setHasSearched(false);
      setResults([]);
      setSearchQuery('');
    }
  }, [open, searchTerms]);

  const handleSearch = async () => {
    setIsSearching(true);
    setResults([]);
    setHasSearched(true);

    try {
      const { data, error } = await supabase.functions.invoke('visual-search', {
        body: { 
          searchTerms, 
          title, 
          category,
          countryCode: regionalSettings.countryCode,
          currency: regionalSettings.currency,
        }
      });

      if (error) {
        console.error('Search error:', error);
        toast.error('Search failed', {
          description: 'Could not find similar products. Please try again.',
        });
        return;
      }

      if (data?.success && data?.data) {
        setResults(data.data.results || []);
        setSearchQuery(data.data.query || '');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed', {
        description: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <DraggableBottomSheet
          open={open}
          onOpenChange={onOpenChange}
          title={
            <span className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Find Similar Products
            </span>
          }
          height="85vh"
        >
          <div className="flex flex-col h-full overflow-y-auto px-5 pb-8">
            {/* Search Info */}
            <div className="mb-4 p-3 bg-surface-2 rounded-xl">
              <p className="text-sm text-muted-foreground">
                Searching for products similar to:
              </p>
              <p className="font-medium text-foreground mt-1">{title}</p>
              {searchQuery && (
                <p className="text-xs text-muted-foreground mt-2">
                  Query: "{searchQuery}"
                </p>
              )}
            </div>

            {/* Loading State */}
            {isSearching && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Searching the web...</p>
              </div>
            )}

            {/* Results */}
            {!isSearching && results.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Found {results.length} similar products
                </p>
                <div className="space-y-3">
                  {results.map((result, index) => (
                    <a
                      key={index}
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-3 p-3 rounded-xl border border-border bg-surface-2 hover:bg-surface-3 transition-colors group"
                    >
                      {/* Image */}
                      <div className="w-16 h-16 rounded-lg bg-surface-3 flex-shrink-0 overflow-hidden">
                        {result.image ? (
                          <img 
                            src={result.image} 
                            alt={result.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground text-sm line-clamp-2 group-hover:text-primary transition-colors">
                          {result.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          {result.price && (
                            <span className="text-primary font-semibold text-sm">
                              {result.price}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {result.store}
                          </span>
                        </div>
                        {result.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {result.description}
                          </p>
                        )}
                      </div>

                      {/* External Link Icon */}
                      <div className="flex items-center">
                        <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {!isSearching && hasSearched && results.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                <Search className="w-12 h-12 text-muted-foreground/50" />
                <div>
                  <p className="font-medium text-foreground">No results found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try saving the image and searching with different terms
                  </p>
                </div>
              </div>
            )}

            {/* Retry Button */}
            {!isSearching && hasSearched && (
              <button
                onClick={handleSearch}
                className="w-full mt-4 py-3 rounded-xl border border-border bg-surface-2 hover:bg-surface-3 font-medium text-foreground transition-colors"
              >
                Search Again
              </button>
            )}
          </div>
        </DraggableBottomSheet>
      )}
    </AnimatePresence>
  );
}
